import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { API_URL } from "../config";

import {
  ArrowRight,
  ArrowLeft,
  Check,
  Mail,
  Smartphone,
  ShieldCheck,
  IdCard,
  Lock,
} from "lucide-react";

export default function SetVerification() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { uploadedFile, recipients, message, signingOrder } = state || {};

  const steps = ["Upload Document", "Add Recipients", "Set Verification Method", "Review & Send"];
  const currentStep = 2;

  const [verificationMethod, setVerificationMethod] = useState("");
  const selectedMethod = verificationMethod;

  const [methods, setMethods] = useState([]);

  // =====================================
  // LOAD IDENTITIES
  // =====================================
  useEffect(() => {

    const fetchIdentities =
      async () => {

        try {

          const user =
            JSON.parse(
              localStorage.getItem(
                "user"
              )
            );

          if (!user?.id)
            return;

          const res =
            await axios.get(
              `${API_URL}/api/user/identity-status/${user.id}`
            );

          setMethods(
            res.data
              ?.identities || []
          );

        } catch (err) {

          console.error(
            "Identity fetch error:",
            err
          );
        }
      };

    fetchIdentities();

  }, []);

  // =====================================
  // GET ICON
  // =====================================
  const getMethodIcon = (
    title
  ) => {

    switch (title) {

      case "Email OTP":
        return (
          <Mail size={18} />
        );

      case "Mobile OTP":
        return (
          <Smartphone
            size={18}
          />
        );

      case "Digital Certificate (PKI)":
        return (
          <ShieldCheck
            size={18}
          />
        );

      case "ID Verification":
        return (
          <IdCard
            size={18}
          />
        );

      default:
        return (
          <Lock
            size={18}
          />
        );
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div className="max-w-5xl mx-auto w-full">

        {/* STEP HEADER */}
        <div className="flex items-center justify-between relative mb-2" style={{ minHeight: "66px" }}>
          {steps.map((step, index) => {
            const isDone = index < currentStep;
            const isCurrent = index === currentStep;

            let circleClass = "bg-gray-200 text-gray-600";
            let labelClass = "text-gray-500";
            if (isDone) {
              circleClass = "bg-green-500 text-white";
              labelClass = "text-green-600";
            } else if (isCurrent) {
              circleClass = "bg-blue-600 text-white";
              labelClass = "text-blue-600";
            }

            return (
            <div key={step} className="flex-1 flex flex-col items-center relative">
              {index !== steps.length - 1 && (
                <div
                  className={`absolute top-5 left-1/2 w-full h-[2px] ${
                    index < currentStep ? "bg-green-500" : "bg-gray-300"
                  }`}
                />
              )}
              <div
                className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${circleClass}`}
                style={{ width: "40px", height: "40px" }}
              >
                {index < currentStep ? <Check size={14} /> : index + 1}
              </div>
              <p
                className={`mt-1 text-[11px] font-medium text-center ${labelClass}`}
              >
                {step}
              </p>
            </div>
            );
          })}
        </div>

        {/* TITLE */}
        <p style={{ fontSize: "24px", fontWeight: 700, color: "#1F2937" }} className="mb-2">Select Verification Method</p>

        {/* METHODS */}
        <div className="space-y-2 max-w-3xl mx-auto w-full">
          {methods.map((method) => (
            <div
              key={method.title}
              onClick={() => setVerificationMethod(method.title)}
              className={`border-2 rounded-xl px-4 py-3 cursor-pointer transition relative ${
                selectedMethod === method.title
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-200 hover:border-blue-300"
              }`}
            >
              {selectedMethod === method.title && (
                <div className="absolute top-3 right-4 bg-green-100 text-green-700 text-[11px] px-2 py-1 rounded-full font-medium">
                  Recommended
                </div>
              )}
              <div className="flex items-start gap-4">
                <input
                  type="radio"
                  name="verificationMethod"
                  checked={selectedMethod === method.title}
                  onChange={() => setVerificationMethod(method.title)}
                  className="mt-1"
                />
                <div className="text-blue-600 mt-[2px]">{getMethodIcon(method.title)}</div>
                <div className="flex-1">
                  <h3 style={{ fontSize: "16px", fontWeight: 500, color: "#1F2937" }}>{method.title}</h3>
                  <p style={{ fontSize: "16px", fontWeight: 400, color: "#6B7280", marginTop: "2px" }}>{method.description}</p>
                  <div className="mt-1">
                    {method.verified ? (
                      <span className="text-green-600 text-xs font-medium">✅ Verified</span>
                    ) : (
                      <span className="text-gray-400 text-xs">Not verified</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* INFO BOX */}
        {selectedMethod && (
          <div className="mt-2 border border-blue-200 bg-blue-50 rounded-xl px-4 py-2 flex items-start gap-3 max-w-3xl mx-auto w-full">
            <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0">i</div>
            <p className="text-sm font-normal text-blue-700">You can manage and customize verification methods in Settings.</p>
          </div>
        )}

        {/* FOOTER */}
        <div className="flex justify-between mt-2 pb-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 border border-gray-300 rounded-[10px] text-gray-700 hover:bg-gray-50 transition text-sm"
            style={{ height: "44px", padding: "0 24px" }}
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <button
            onClick={() => {
              if (!verificationMethod) {
                alert("Please select a verification method");
                return;
              }
              navigate("/review-and-send", { state: { uploadedFile, recipients, message, verificationMethod, signingOrder } });
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-[10px] transition text-sm"
            style={{ height: "44px", padding: "0 24px" }}
          >
            Next
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}